import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import { Icon, Image } from "semantic-ui-react";

import { useIntl } from "react-intl";

import StaffChatBox from "./StaffChatBox";

type CardDialogChatListProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  chatController: any;
  allAdminChatRoomList?: Record<string, any>[];
  apiToken: string;
  divisionId: string;
  patientHNChatList?: string[];
  userId: string;
};

type Styles = Record<"chatContainer" | "header", CSSProperties>;

const styles: Styles = {
  chatContainer: {
    backgroundColor: "white",
    border: "1px solid rgba(228, 218, 218, 0.5)",
    borderRadius: "0 0 5px 5px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    height: "400px",
    overflow: "hidden",
  },
  header: {
    alignItems: "center",
    backgroundColor: "rgba(255, 229, 229, 1)",
    borderRadius: "5px 5px 0 0",
    display: "flex",
    height: "53px",
    padding: "0 1rem 0 1.25rem",
    width: "100%",
  },
};

const IMAGES = {
  person: "/static/images/person.png",
};

const CardDialogChatList = (props: CardDialogChatListProps) => {
  const intl = useIntl();

  const chatBoxRefs = useRef<Record<string, any>>({});
  const [minimizedChats, setMinimizedChats] = useState<Set<number>>(() => new Set());

  const filteredChatList = useMemo(() => {
    const hnList = props.patientHNChatList ?? [];
    const chatList = props.allAdminChatRoomList ?? [];

    return chatList.filter((item) => hnList.includes(item.hn));
  }, [props.allAdminChatRoomList, props.patientHNChatList]);

  useEffect(() => {
    const currentChatIds = new Set(filteredChatList.map((chat) => Number(chat.id)));

    setMinimizedChats((prev) => {
      // กรอง chatId ที่ยังอยู่ใน filteredChatList เท่านั้น
      const updated = new Set([...prev].filter((chatId) => currentChatIds.has(chatId)));

      return updated.size === prev.size ? prev : updated;
    });
  }, [filteredChatList]);

  const handleMinimize = (chatId: number) => {
    setMinimizedChats((prev) => {
      const updatedSet = new Set(prev);

      if (updatedSet.has(chatId)) {
        updatedSet.delete(chatId);
      } else {
        updatedSet.add(chatId);
      }

      return updatedSet;
    });
  };

  const handleClose = (hn: string) => {
    const hnList = props.patientHNChatList ?? [];
    const filtered = hnList.filter((value) => value !== hn);

    props.setProp("patientHNChatList", filtered);
  };

  const handleClickLink = (e: MouseEvent) => {
    const a = e.target as HTMLAnchorElement;
    const href = a.href || "";

    const clickLeft = e.button === 0;

    if (clickLeft && href) {
      window.open(href, "_blank", "noopener noreferrer")?.focus();
    }
  };

  return (
    <div style={{ bottom: 0, position: "fixed", right: 0, zIndex: 1001 }}>
      <div
        style={{ alignItems: "flex-end", display: "flex", gap: "1rem", padding: "0 1rem 0rem 0" }}
      >
        {filteredChatList.map((item) => {
          const isMinimized = minimizedChats.has(item.id);

          return (
            <div key={`chat-list-${item.id}`} style={{ width: "375px" }}>
              <div
                style={{
                  ...styles.header,
                  ...(isMinimized && { borderRadius: "5px" }),
                }}
              >
                <Image
                  style={{
                    borderRadius: "50%",
                    height: "35px",
                    marginRight: "0.55rem",
                    width: "35px",
                  }}
                  alt="chat-image.icon"
                  src={item.image || IMAGES.person}
                />
                <div
                  style={{
                    flex: 1,
                    fontSize: "14px",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.chat_channel_name_autogen || item.name || "Chat"}
                </div>
                <Icon
                  style={{
                    color: "rgba(105, 104, 104, 1)",
                    cursor: "pointer",
                    margin: "-13px 1rem 0 0",
                  }}
                  name="window minimize"
                  onClick={() => {
                    handleMinimize(item.id);
                  }}
                />
                <Icon
                  style={{
                    color: "rgba(105, 104, 104, 1)",
                    cursor: "pointer",
                    marginTop: "-4px",
                  }}
                  name="close"
                  onClick={() => {
                    handleClose(item.hn);
                  }}
                />
              </div>
              <div
                className="admin-menu-chat"
                style={{
                  ...styles.chatContainer,
                  display: isMinimized ? "none" : "block",
                }}
              >
                <StaffChatBox
                  ref={(ref: any) => {
                    chatBoxRefs.current[item.id] = ref;
                  }}
                  onEvent={props.onEvent}
                  chatController={props.chatController}
                  apiToken={props.apiToken}
                  chatBoxStyle={{ height: "400px" }}
                  divisionId={props.divisionId}
                  placeholder={intl.formatMessage({ id: "พิมพ์ข้อความ" })}
                  selectedChat={item}
                  toggleSearch={false}
                  userId={props.userId}
                  hideCallButton
                  isChatPatientOnly
                  menuSameLevelTextArea
                  onClickLink={handleClickLink}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardDialogChatList;
